import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, RefreshCw, X, MapPin, Star, Mail, Download } from "lucide-react";

const PRIVACY_OPTIONS = ["public", "city_only", "private", "business"];
const STATUS_PILL = {
  unregistered: ["UNREGISTERED", "border-white/20 text-[#9a9690]"],
  registered: ["REGISTERED", "border-[#5fc377]/40 text-[#5fc377]"],
};

export default function AdminTruths() {
  const [truths, setTruths] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, a] = await Promise.all([http.get("/admin/truths"), http.get("/artworks")]);
      setTruths(t.data); setArtworks(a.data || []);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (!window.confirm("Assign Truth IDs to all paid orders that don't have them?")) return;
    setBusy(true);
    try {
      const { data } = await http.post("/admin/truths/backfill");
      toast.success(`Created ${data.created} truth(s)`);
      load();
    } catch { toast.error("Backfill failed"); }
    finally { setBusy(false); }
  };

  const startNew = () => setEditing({
    isNew: true, privacy: "city_only", artwork_id: artworks[0]?.id || "",
    artwork_title: artworks[0]?.title || "", city: "", state: "", country: "US",
    owner_display_name: "", business_name: "", business_website: "", business_logo_url: "",
    co_owner_display_name: "", co_owner_email: "", featured: false, owner_email: "",
    story_reads: 0, qr_scans: 0,
  });
  const startEdit = (t) => setEditing({ ...t });

  const save = async () => {
    setBusy(true);
    try {
      if (editing.isNew) {
        const art = artworks.find((a) => a.id === editing.artwork_id);
        await http.post("/admin/truths", { ...editing, artwork_title: art?.title || editing.artwork_title });
        toast.success("Truth created");
      } else {
        await http.patch(`/admin/truths/${editing.truth_id}`, {
          privacy: editing.privacy, owner_display_name: editing.owner_display_name,
          business_name: editing.business_name, business_website: editing.business_website,
          business_logo_url: editing.business_logo_url,
          co_owner_display_name: editing.co_owner_display_name, co_owner_email: editing.co_owner_email,
          city: editing.city, state: editing.state, country: editing.country,
          story_reads: Number(editing.story_reads) || 0, qr_scans: Number(editing.qr_scans) || 0,
          status: editing.status, featured: !!editing.featured,
        });
        toast.success("Truth updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (tid) => {
    if (!window.confirm(`Delete ${tid}? This cannot be undone.`)) return;
    try { await http.delete(`/admin/truths/${tid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (t) => {
    try {
      await http.patch(`/admin/truths/${t.truth_id}`, { featured: !t.featured });
      toast.success(t.featured ? "Removed from Featured" : "Marked as Featured ✨");
      load();
    } catch { toast.error("Failed"); }
  };

  const emailCert = async (tid) => {
    if (!window.confirm(`Email the Truth Certificate PDF to the owner of ${tid}?`)) return;
    try {
      const { data } = await http.post(`/admin/truths/${tid}/email-certificate`);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const downloadCert = async (tid, size = "letter") => {
    try {
      const { data } = await http.get(`/truths/${tid}/certificate.pdf?size=${size}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not load PDF"); }
  };

  const uploadBusinessLogo = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
        setEditing((e) => ({ ...e, business_logo_url: data.url }));
        toast.success("Logo uploaded");
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="admin-truths">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="font-display text-2xl text-[#F4F1EA]">Truth Registry</div>
          <div className="font-body text-[13px] text-[#8a857d] mt-1">Manage every truth — backfill from paid orders, add manually, edit privacy/location/counters, or delete. {truths.length} total. <span className="text-[#9a9690]">Digital downloads do not receive Truth IDs (physical prints only).</span></div>
        </div>
        <div className="flex gap-2">
          <button onClick={backfill} disabled={busy} data-testid="truths-backfill-btn" className="px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2 disabled:opacity-40">
            <RefreshCw size={13}/> Backfill From Orders
          </button>
          <button onClick={startNew} data-testid="truths-new-btn" className="px-4 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
            <Plus size={13}/> New Truth
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a]">
        {truths.length === 0 ? (
          <div className="p-10 text-center font-body text-[#8a857d] italic">No truths yet. Click <strong className="text-[#C9A055]">Backfill From Orders</strong> to create them from your existing paid orders, or <strong className="text-[#C9A055]">New Truth</strong> to add one manually.</div>
        ) : (
          <table className="w-full">
            <thead className="text-[10px] tracking-[0.18em] text-[#8a857d] uppercase">
              <tr className="border-b border-white/10">
                <th className="text-left p-3">Truth ID</th>
                <th className="text-left p-3">Artwork</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Privacy</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Reads / Scans</th>
                <th className="text-left p-3">★</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {truths.map((t) => {
                const [pillLabel, pillCls] = STATUS_PILL[t.status] || ["?", "border-white/15 text-[#9a9690]"];
                return (
                  <tr key={t.truth_id} className="border-b border-white/5 hover:bg-white/3" data-testid={`truth-row-${t.truth_id}`}>
                    <td className="p-3 font-ui text-[11px] tracking-[0.22em] text-[#C9A055]">{t.truth_id}</td>
                    <td className="p-3 font-body text-[13px] text-[#F4F1EA]">{t.artwork_title}<div className="font-ui text-[10px] text-[#8a857d] mt-0.5">{t.variant_label}</div></td>
                    <td className="p-3"><span className={`inline-block px-2 py-0.5 border font-ui text-[9px] tracking-[0.22em] ${pillCls}`}>{pillLabel}</span></td>
                    <td className="p-3 font-body text-[12px] text-[#B5B0A7]">{(t.privacy || "—").toUpperCase()}</td>
                    <td className="p-3 font-body text-[12px] text-[#B5B0A7]">{t.city ? `${t.city}, ${t.state || ""}` : "—"}{t.lat && <span className="text-[#5fc377] ml-1">📍</span>}</td>
                    <td className="p-3 font-body text-[12px] text-[#B5B0A7]">{t.story_reads || 0} / {t.qr_scans || 0}</td>
                    <td className="p-3">
                      <button onClick={() => toggleFeatured(t)} data-testid={`truth-feature-${t.truth_id}`} title={t.featured ? "Unfeature" : "Mark as Featured"} className={`p-1 ${t.featured ? "text-[#C9A055]" : "text-[#3a3631] hover:text-[#C9A055]"}`}>
                        <Star size={14} fill={t.featured ? "#C9A055" : "none"}/>
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => downloadCert(t.truth_id)} data-testid={`truth-cert-${t.truth_id}`} title="View Certificate PDF" className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055]"><Download size={13}/></button>
                        <button onClick={() => emailCert(t.truth_id)} data-testid={`truth-email-${t.truth_id}`} title="Email Certificate to Owner" className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055]"><Mail size={13}/></button>
                        <button onClick={() => startEdit(t)} data-testid={`truth-edit-${t.truth_id}`} title="Edit" className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055]"><Edit3 size={13}/></button>
                        <button onClick={() => remove(t.truth_id)} data-testid={`truth-delete-${t.truth_id}`} title="Delete" className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-2xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="truth-modal">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">{editing.isNew ? "NEW TRUTH" : editing.truth_id}</div>
                <div className="font-display text-xl text-[#F4F1EA]">{editing.isNew ? "Create Truth" : "Edit Truth"}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={20}/></button>
            </div>
            <div className="p-6 space-y-4">
              {editing.isNew && (
                <div>
                  <Lbl>Artwork</Lbl>
                  <select className={inputCls} value={editing.artwork_id} onChange={(e) => setEditing({ ...editing, artwork_id: e.target.value })} data-testid="truth-artwork-select">
                    {artworks.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Lbl>Privacy</Lbl>
                <select className={inputCls} value={editing.privacy || "city_only"} onChange={(e) => setEditing({ ...editing, privacy: e.target.value })} data-testid="truth-privacy">
                  {PRIVACY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <label className="flex items-center gap-3 border border-white/10 bg-black/40 p-3 cursor-pointer">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#C9A055]" data-testid="truth-featured"/>
                <div>
                  <div className="font-ui text-[11px] text-[#C9A055] tracking-[0.22em] uppercase">★ Featured Truth Home</div>
                  <div className="font-body text-[11px] text-[#8a857d]">Show this truth in the hero strip on /registry (top 5 most recent featured).</div>
                </div>
              </label>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field testid="truth-display-name" label="Owner display name" value={editing.owner_display_name} onChange={(v) => setEditing({ ...editing, owner_display_name: v })}/>
                <Field testid="truth-business-name" label="Business name" value={editing.business_name} onChange={(v) => setEditing({ ...editing, business_name: v })}/>
              </div>

              {editing.privacy === "business" && (
                <div className="border border-[#C9A055]/20 bg-black/30 p-3 space-y-3">
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">BUSINESS DETAILS</div>
                  <Field testid="truth-biz-website" label="Website (https://…)" value={editing.business_website} onChange={(v) => setEditing({ ...editing, business_website: v })}/>
                  <div>
                    <Lbl>Business logo</Lbl>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 border border-white/10 bg-black flex items-center justify-center overflow-hidden">
                        {editing.business_logo_url ? <img src={editing.business_logo_url.startsWith("http") ? editing.business_logo_url : `${process.env.REACT_APP_BACKEND_URL}${editing.business_logo_url}`} alt="logo" className="max-w-full max-h-full object-contain"/> : <span className="text-[9px] text-[#5a5650]">none</span>}
                      </div>
                      <label className="px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.22em] uppercase cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBusinessLogo(e.target.files?.[0])} data-testid="truth-biz-logo-input"/>
                        Upload
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-white/10 bg-black/30 p-3 space-y-3">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">CO-OWNER (OPTIONAL)</div>
                <p className="font-body text-[11px] text-[#8a857d]">Gift recipient or co-keeper. Shown publicly on public/business truths.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field testid="truth-coowner-name" label="Co-owner display name" value={editing.co_owner_display_name} onChange={(v) => setEditing({ ...editing, co_owner_display_name: v })}/>
                  <Field testid="truth-coowner-email" label="Co-owner email" value={editing.co_owner_email} onChange={(v) => setEditing({ ...editing, co_owner_email: v })}/>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <Field testid="truth-city" label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })}/>
                <Field testid="truth-state" label="State" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })}/>
                <Field testid="truth-country" label="Country" value={editing.country} onChange={(v) => setEditing({ ...editing, country: v })}/>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field testid="truth-reads" label="Story Reads" type="number" value={editing.story_reads} onChange={(v) => setEditing({ ...editing, story_reads: v })}/>
                <Field testid="truth-scans" label="QR Scans" type="number" value={editing.qr_scans} onChange={(v) => setEditing({ ...editing, qr_scans: v })}/>
              </div>
              <div className="font-body text-[11px] text-[#8a857d] border border-white/5 p-3 bg-black/30">
                <MapPin size={10} className="inline mr-1"/> City/state auto-geocoded with OpenStreetMap. Saving may take 1-2 seconds while we look up the coordinates.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-[#9a9690] font-ui text-[10px] tracking-[0.32em] uppercase">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="truth-save-btn" className="px-5 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none";
const Lbl = ({ children }) => <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{children}</div>;

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <Lbl>{label}</Lbl>
      <input type={type} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
    </label>
  );
}
